/* eslint-disable */
import log from '@/logger';
import api from '@/services/api';
import ls from '@/helpers/localStorage';
import store from '@/store';
import { isAndroid, isIE, isIos } from '@/mixins/userAgent';

const MESSAGING_TOKEN_FLAG_NAME = 'firebase-token-sent';
const MESSAGING_TOKEN_EXPIRES_AT = 31536000;
const REF_ORDER_BOOK = 'contractOrderBook';
const REF_MARKETS = 'marketStats';
const REF_CONTRACTS = 'contractStats';

const EVENT_ADDED = 'child_added';
const EVENT_CHANGED = 'child_changed';
const EVENT_INIT = 'value';

const DATA_METHOD = 'update';
const DATA_MARKETS = 'market_data';
const DATA_CONTRACTS = 'contract_data';
// let vapidKey = '';

const setSentTokenFlag = flag => ls.set(MESSAGING_TOKEN_FLAG_NAME, flag, MESSAGING_TOKEN_EXPIRES_AT);
const sendTokenToServer = (token) => {
  if (!store.getters['user/isLoggedIn']) {
    log.info('firebase::', 'sendTokenToServer skipped', 'User is not logged in');
    return;
  }

  const isTokenSent = ls.get(MESSAGING_TOKEN_FLAG_NAME, false);
  if (isTokenSent) {
    log.info('firebase::', 'sendTokenToServer skipped', 'Token was already sent');
    return;
  }

  api.account.putMessagingToken(token)
    .then(() => setSentTokenFlag(true))
    .catch(() => setSentTokenFlag(false));
};

const connect = (token = null, listenerOptions) => {
  let config = {};
  const isAnonymous = token === null;
  if (isAnonymous) {
    config = {
      apiKey: process.env.FIREBASE_API_KEY_ANONYMOUS,
      authDomain: process.env.FIREBASE_AUTH_DOMAIN_ANONYMOUS,
      databaseURL: process.env.FIREBASE_DATABASE_URL_ANONYMOUS,
      projectId: process.env.FIREBASE_PROJECT_ID_ANONYMOUS,
      messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID_ANONYMOUS,
      appId: process.env.FIREBASE_APP_ID_ANONYMOUS
    };
    // vapidKey = process.env.FIREBASE_VAPID_ANONYMOUS;
  } else {
    config = {
      apiKey: process.env.FIREBASE_API_KEY,
      authDomain: process.env.FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.FIREBASE_DATABASE_URL,
      projectId: process.env.FIREBASE_PROJECT_ID,
      messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.FIREBASE_APP_ID
    };
    // vapidKey = process.env.FIREBASE_VAPID;
  }
  log.info('firebase::', 'init', `anonymous mode: ${isAnonymous}`, config);
  if (window.firebase.apps.length > 0) {
    log.info('firebase::', 'reinitializing app');
    window.firebase.app().delete().then(() => {
      log.info('firebase::', 'reinitialized app succesfully');
      window.firebase.initializeApp(config);
      store.dispatch('app/setFirebaseInitialized', true);
      addListener(listenerOptions);
    });
  } else {
    window.firebase.initializeApp(config);
    store.dispatch('app/setFirebaseInitialized', true);
    addListener(listenerOptions);
  }
};

const triggerUpdate = ({ loaded = false, callback, method = DATA_METHOD, name, data}) => {
  if (loaded) {
    log.info('firebase::', `${method} of ${name} with ${JSON.stringify(data)}`);
    callback(method, name, data);
  } else {
    log.info('firebase::', `skipping ${method} of ${name}`);
  }
};

const addOrderBookListener = (contractId, listener, defaultValue = null) => {
  /* so initial snapshot is ignored -it should come from API */
  let loaded = false;
  const orderBook = window.firebase.database().ref(`${REF_ORDER_BOOK}/${contractId}`);
  orderBook.on(EVENT_INIT, (snapshot) => {
    const data = snapshot.val();
    if (process.env.ORDER_BOOK_API_CALL_ENABLED && !loaded) {
      log.info('firebase::', 'skipping initial order book update');
    } else if (data) {
      log.info('firebase::', `update order book ${JSON.stringify(data)}`);
      listener(data);
    } else if (defaultValue !== null) {
      log.info('firebase::', 'update order book with default value');
      listener(defaultValue);
    } else {
      log.info('firebase::', 'order book is empty');
    }
    loaded = true;
  });
};

const clearOrderBookListeners = (contractId) => {
  window.firebase.database().ref(`${REF_ORDER_BOOK}/${contractId}`).off();
};

const addListener = (options) => {
  log.info('firebase::', 'adding listener');
  const { onUpdate = () => null } = options;
  let marketDataLoaded = false;
  let contractDataLoaded = false;

  const markets = window.firebase.database().ref(REF_MARKETS).orderByChild('TimeStamp')
  .startAt((+new Date()/1000).toFixed(5).toString());
  markets.on(EVENT_ADDED, (data) => {
    triggerUpdate({
      loaded: marketDataLoaded,
      callback: onUpdate,
      name: DATA_MARKETS,
      data: data.val()
    });
  });
  markets.on(EVENT_CHANGED, (data) => {
    triggerUpdate({
      loaded: marketDataLoaded,
      callback: onUpdate,
      name: DATA_MARKETS,
      data: data.val()
    });
  });
  markets.once(EVENT_INIT, () => {
    marketDataLoaded = true;
  });

  const contracts = window.firebase.database().ref(REF_CONTRACTS).orderByChild('TimeStamp')
  .startAt((+new Date()/1000).toFixed(5).toString());

  contracts.on(EVENT_ADDED, (data) => {
    triggerUpdate({
      loaded: contractDataLoaded,
      callback: onUpdate,
      name: DATA_CONTRACTS,
      data: data.val()
    });
  });
  contracts.on(EVENT_CHANGED, (data) => {
    triggerUpdate({
      loaded: contractDataLoaded,
      callback: onUpdate,
      name: DATA_CONTRACTS,
      data: data.val()
    });
  });
  contracts.once(EVENT_INIT, () => {
    contractDataLoaded = true;
  });
};

const setupMessaging = () => {
  if (isAndroid || isIos || isIE) return;

  const messaging = window.firebase.messaging();
  // if (vapidKey) {
  //   log.info('firebase::', 'setting up vapidKey', vapidKey);
  //   messaging.usePublicVapidKey(vapidKey);
  // } else {
  //   log.info('firebase::', 'usePublicVapidKey skipped');
  // }
  log.info('firebase::', 'setting up messaging');
  messaging.requestPermission()
      .then((data) => {
        log.info('firebase::', 'permission granted', data);
        messaging.getToken()
          .then((token) => {
              log.info('firebase::', 'messaging token received');
              if (token) {
                sendTokenToServer(token);
              } else {
                setSentTokenFlag(false);
              }
          }).catch((error) =>  {
            log.info('firebase::', 'error requesting token', error);
            setSentTokenFlag(false)
        });
        messaging.onTokenRefresh(() => {
          messaging.getToken()
            .then((token) => {
              log.info('firebase::', 'messaging token refreshed');
              if (token) {
                setSentTokenFlag(false);
                sendTokenToServer(token);
              }
            }).catch((error) =>  {
            log.info('firebase::', 'error requesting refresh token', error);
            setSentTokenFlag(false)
        });
        });
      })
    .catch((error) => {
      log.info('firebase::', 'error requesting permission', error);
    });
};


const cleanupMessaging = () => ls.del(MESSAGING_TOKEN_FLAG_NAME);

export {
  connect,
  addListener,
  addOrderBookListener,
  clearOrderBookListeners,
  setupMessaging,
  cleanupMessaging,
};




// WEBPACK FOOTER //
// ./src/services/notifications/firebase.js